import { invoke } from '@shared/easyfy.decorators';
import { addCss, removeCss, toggleClass, debounce } from '@shared/easyfy.utils';
import { globalEvent } from '@shared/easyfy.core';
import { GetLoginFlyout } from '@clientWebServices/header.service';
import MessageService from '@clientWebServices/message.service';

@invoke
export default class Menu {
  static Instance: Menu;
  private activeMenu: HTMLElement | null = document.querySelector('.mm-active .nav-list');
  private navHeader: HTMLElement | null = document.querySelector('.mm-active .nav-header');
  private isMenuLoginLoaded = false;

  private timer: any;
  private mainSection: HTMLElement | null = document.querySelector('main');
  private mainMenuSection: HTMLElement | null = document.querySelector('[data-container="main-menu"]');
  private activeMainMenuContainer: HTMLElement | null = null;

  private focusOutHandler: (e: FocusEvent) => void = (event: FocusEvent) => {
    this.focusOutMenu(event);
  };
  private keyupHandler: (e: KeyboardEvent) => void = (event: KeyboardEvent) => {
    this.escapeMenu(event);
  };

  private debouncedUpdateMenuStyle = debounce(() => {
    this.updateMenuStyle();
  }, 200);

  onInit() {
    globalEvent({
      type: 'click',
      events: [
        {
          event: 'toggle-menu-children',
          callback: this.toggleMenuChildren,
        },
        {
          event: 'menu-goback',
          callback: this.menuGoBack,
        },
        {
          event: 'scroll-main-nav',
          callback: this.scrollMainNav,
        },
        {
          event: 'toggle-desktop-menu',
          callback: this.toggleDesktopMenu,
        },
        {
          action: 'toggle-menu-login',
          callback: this.toggleMenuLogin,
        },
      ],
    });

    this.updateMenuStyle();

    window.addEventListener('resize', this.debouncedUpdateMenuStyle);
  }

  private updateMenuStyle() {
    const navList = document.querySelector<HTMLElement>('[data-container="nav-list"]');
    if (!navList) return;

    const navItems = document.querySelectorAll<HTMLElement>('.menu-item.level-1');
    if (navItems.length === 0) return;

    const LINE_BREAK_CLASS_NAME = 'line-break';

    let updated = false;
    navItems.forEach((itemEl) => {
      if (itemEl.offsetTop > navList.offsetTop && !updated) {
        itemEl.classList.add(LINE_BREAK_CLASS_NAME);
        updated = true;
      } else if (itemEl.classList.contains(LINE_BREAK_CLASS_NAME)) {
        itemEl.classList.remove(LINE_BREAK_CLASS_NAME);
      }
    });
  }

  private scrollMainNav = (e: Event) => {
    let element = e.target as HTMLElement;

    if (!element.hasAttribute('data-target')) {
      const parent = element.parentElement;
      if (parent) {
        element = parent;
      }
    }

    const scrollNumber = 200;
    const target = element.getAttribute('data-target');
    const direction = element.getAttribute('data-scroll-direction');
    const container = document.querySelector(`[data-container="${target}"`) as HTMLElement | null;
    if (!container) {
      console.error(`Container with data-container="${target}" not found.`);
      return false;
    }
    const scrollRight = direction === 'right';

    const leftArrow = document.querySelector<HTMLElement>('[data-event="scroll-main-nav"][data-scroll-direction="left"]');
    const rightArrow = document.querySelector<HTMLElement>('[data-event="scroll-main-nav"][data-scroll-direction="right"]');

    let scroll: number = scrollRight ? scrollNumber : -scrollNumber;
    let scrollPosition: number = container.scrollLeft + scroll;
    const scrollWidth: number = container.scrollWidth;
    const leftToScroll: number = scrollWidth - (scrollPosition + container.clientWidth);

    const VISIBLE_CLASS_NAME = 'visible';

    if (scrollRight && leftToScroll < scroll) {
      scroll += leftToScroll;
      scrollPosition += leftToScroll;
    } else if (!scrollRight && scrollPosition < leftToScroll) {
      scroll -= leftToScroll;
      scrollPosition -= leftToScroll;
    }

    container.scrollBy({
      left: scroll,
      behavior: 'smooth',
    });

    if (!leftArrow || !rightArrow) return false;
    if (scrollPosition <= 0) {
      removeCss(leftArrow, VISIBLE_CLASS_NAME);
    } else {
      addCss(leftArrow, VISIBLE_CLASS_NAME);
    }

    if (scrollWidth <= scrollPosition + container.clientWidth) {
      removeCss(rightArrow, VISIBLE_CLASS_NAME);
    } else {
      addCss(rightArrow, VISIBLE_CLASS_NAME);
    }

    return false;
  };

  private focusOutMenu = (e: FocusEvent) => {
    const relatedTarget = e.relatedTarget;

    if (!(relatedTarget instanceof Node) || !this.activeMainMenuContainer?.contains(relatedTarget)) {
      this.closeDesktopMenu();
    }
  };

  private escapeMenu = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      this.closeDesktopMenu();
    }
  };

  private toggleDesktopMenu = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    const element = e.target;
    if (!(element instanceof HTMLElement)) return;
    if (element.getAttribute('aria-expanded') === 'true') {
      this.closeDesktopMenu();
      return;
    }
    const container = document.querySelector(`[data-container="${element.dataset.target}"]`);

    if (!(container instanceof HTMLElement)) return;

    this.activeMainMenuContainer = container;
    this.mainMenuSection?.addEventListener('focusout', this.focusOutHandler);
    this.mainMenuSection?.addEventListener('keyup', this.keyupHandler);

    toggleClass(container, 'visible');
    element.setAttribute('aria-expanded', 'true');

    if (container.classList.contains('visible')) {
      this.timer = setTimeout(() => {
        if (this.mainSection) addCss(this.mainSection, 'menu-overlay');
      }, 400);
    } else {
      if (this.mainSection) removeCss(this.mainSection, 'menu-overlay');
      clearTimeout(this.timer);
    }
  };

  private closeDesktopMenu = () => {
    this.mainMenuSection?.removeEventListener('focusout', this.focusOutHandler);
    this.mainMenuSection?.removeEventListener('keyup', this.keyupHandler);

    if (this.activeMainMenuContainer) {
      removeCss(this.activeMainMenuContainer, 'visible');
      this.activeMainMenuContainer = null;
    }
    document.querySelectorAll('[data-event="toggle-desktop-menu"]').forEach((element) => {
      if (element instanceof HTMLElement) {
        element.setAttribute('aria-expanded', 'false');
      }
    });
    if (this.mainSection) removeCss(this.mainSection, 'menu-overlay');

    clearTimeout(this.timer);
  };

  private toggleMenuChildren = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    const element = e.target as HTMLElement;

    if (element.dataset.target != null) {
      Menu.addBreadCrumbMenuItem(element.innerText);

      const childElement = document.querySelector(`[data-container="${element.dataset.target}"`);

      if (childElement) {
        Menu.updateMenuBreadcrumbs();

        const activeElement = document.querySelector('.mm-active');
        if (activeElement) {
          removeCss(activeElement, 'mm-active');
          addCss(activeElement, 'mm-visited');
        }

        addCss(childElement, 'mm-active');

        this.registerMenuScroll();
      }
    }
  };

  private menuGoBack = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    const element = e.target as HTMLElement;
    const container = element?.closest('.mm-active');
    const parent = element?.closest('.mm-visited');

    if (container) {
      removeCss(container, 'mm-active');
      Menu.removeBreadCrumbLastMenuItem();
      Menu.updateMenuBreadcrumbs();
    }

    if (parent) {
      removeCss(parent, 'mm-visited');
      addCss(parent, 'mm-active');
    }
  };

  private registerMenuScroll() {
    // unregister event scroll
    this.activeMenu?.removeEventListener('scroll', this.determineShadowLocation);

    // get active menu element
    this.activeMenu = document.querySelector('.mm-active .nav-list');
    this.navHeader = document.querySelector('.mm-active .nav-header');

    if (!this.activeMenu || !this.navHeader) return;

    const height = window.innerHeight - this.navHeader.offsetHeight;

    //no scroll needed
    if (this.activeMenu.scrollHeight <= height) return;

    this.activeMenu.style.setProperty('--header-height', `${this.navHeader.offsetHeight}px`);

    this.activeMenu.addEventListener('scroll', this.determineShadowLocation.bind(this), { passive: true });
    this.determineShadowLocation();
  }

  private determineShadowLocation = () => {
    const activeMenu = this.activeMenu;
    if (!activeMenu) return;

    if (activeMenu.scrollHeight - activeMenu.scrollTop < activeMenu.clientHeight + 10) {
      //scrolled all the way down
      removeCss(activeMenu, 'bottom');
    } else if (activeMenu.scrollTop > 0) {
      //scrolling
      addCss(activeMenu, ['bottom', 'top']);
    } else {
      //scrolled all the way up
      removeCss(activeMenu, 'top');
    }
  };

  private savedBreadcrumbsList: string[] = [];

  public static updateMenuBreadcrumbs = () => {
    const breadcrumbContainers = document.querySelectorAll('[data-container="menu-breadcrumbs"]');

    breadcrumbContainers?.forEach((el) => {
      el.innerHTML = '';
      Menu.Instance.savedBreadcrumbsList.forEach((menuItem, index) => {
        const isLast = index === Menu.Instance.savedBreadcrumbsList.length - 1;
        const spanElement = document.createElement('span');

        spanElement.textContent = menuItem;

        if (!isLast) {
          const chevronElement = document.createElement('i');
          chevronElement.className = 'icon icon-chevron-right size-xs clr-arrow';
          el.appendChild(spanElement);
          el.appendChild(chevronElement);
        } else {
          el.appendChild(spanElement);
        }
      });
    });
  };

  public static addBreadCrumbMenuItem = (menu: string) => {
    Menu.Instance.savedBreadcrumbsList.push(menu);
  };

  public static removeBreadCrumbLastMenuItem = () => {
    if (Menu.Instance.savedBreadcrumbsList.length > 0) Menu.Instance.savedBreadcrumbsList.pop();
  };

  private toggleMenuLogin = async (e: Event): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    const element = e.target as HTMLElement;
    const target = element.dataset.target ?? element.closest('[data-target]')?.getAttribute('data-target');
    if (!target) return;

    const container = document.querySelector<HTMLElement>(`[data-container="${target}"]`);
    if (!container) return;

    if (!this.isMenuLoginLoaded) {
      await GetLoginFlyout()
        .then((response) => {
          if (response.data?.partialHtml) {
            container.innerHTML = response.data.partialHtml;
            this.isMenuLoginLoaded = true;

            this.updateMenuLoginCloseButton(container);
          }
        })
        .catch(() => {
          MessageService.Instance.displayDefaultErrorMessage();
        });
    }

    const activeElement = document.querySelector('.mm-active');
    if (activeElement) {
      removeCss(activeElement, 'mm-active');
      addCss(activeElement, 'mm-visited');
    }

    addCss(container, 'mm-active');
  };

  private updateMenuLoginCloseButton(container: HTMLElement): void {
    const goBackButton = container.querySelector<HTMLElement>('[data-event="menu-goback"]');
    if (goBackButton) {
      removeCss(goBackButton, 'hidden');
      goBackButton.removeAttribute('aria-hidden');
    }

    const closeButton = container.querySelector<HTMLElement>('[data-event="toggle"][data-target="login-flyout"]');
    if (closeButton) {
      closeButton.dataset.event = 'close-login';
    }
  }
}
